/**
 *  WEB请求库umi-request基于axios
 *  h5请求库axios
 *  小程序请求库uni.request
 *
 *  web和h5支持
 *  1. promise写法
 *  2. 拦截器
 *
 *  小程序封装一层数据处理，兼容axios promise写法，处理请求拦截数据
 *
 */
import dayjs from "dayjs";
import { v4 as uuid4 } from "uuid";
import { ACT, DEFAULT_TEMPLATE } from "../constants";
import { generateId } from "../shared/util";
export const default_user_acl = {
  [ACT.文档编辑]: true,
  [ACT.大纲编辑]: true,
  [ACT.大纲管理]: true
};
export const createDefaultTempProp = (prop = {}, currentUser, status = 0, data = null) => {
  const _sys_temp = [DEFAULT_TEMPLATE, []],
    _sys_task_status = {
      index: status,
      prop: [],
      sender: currentUser,
      sendTime: new Date().getTime()
    };
  if (status === 1) {
    _sys_task_status["prop"] = [data?.owner, data?.participant, data?.startTime, data?.endTime];
  }
  prop["_sys_temp"] = _sys_temp;
  prop["_sys_task_status"] = _sys_task_status;
  return prop;
};

// 通用拦截器
export function awaitWrap(promise) {
  return promise.then(res => [null, res]).catch(err => [err, null]);
}

// 生成任务信息
export function genTaskProp(props) {
  const {
    startTime,
    endTime,
    owner,
    participant,
    isCompleted,
    userId,
    status
  } = props;
  return [{
    id: uuid4(),
    _startTime: startTime ? dayjs(startTime).valueOf() : Date.now(),
    _endTime: endTime || null,
    _description: "",
    _status: status || "action",
    _type: 0,
    _createTime: Date.now(),
    _isCompleted: isCompleted || false,
    _assigneeId: {
      user: participant ? participant : [],
      owner: owner ? [owner] : [userId],
      group: []
    },
    _remindType: null
  }];
}

/**
 * 属性编辑
 * @param {promise} network
 * @param {object} data
 * @example const [err,res] = await updateProp(request,{org_id:xxx,key:a,value:'aaaa' })
 */
export const updateNodeProp = async (network, data = {}) => {
  // 可以在这里处理通用的复杂数据逻辑
  return await awaitWrap(network("/docapi/node/modifyProp", {
    method: "POST",
    data
  }));
};
/**
 * 修改节点属性
 */
export const updateProp = async (network, data = {}) => {
  return await awaitWrap(network("/docapi/updateProp", {
    method: "POST",
    data
  }));
};

/**
 * 获取引用/匹配的选项和节点ID
 */
export const execCalcFormula = async (network, data = {}) => {
  return await awaitWrap(network("/docapi/execCalcFormula", {
    method: "POST",
    data
  }));
};

/**
 * 系统属性编辑
 * @param {promise} network
 * @param {object} data
 */

export const updateNodeSysProp = async (network, data = {}) => {
  return await awaitWrap(network("/docapi/node/modifySysProp", {
    method: "POST",
    data
  }));
};
/**
 * 移动节点
 * @param {promise} network
 * @param {object} data
 */
export const moveNode = async (network, data = {}) => {
  return await awaitWrap(network("/docapi/node/move", {
    method: "POST",
    data
  }));
};

/**
 *  新增节点
 * @param {promise} network
 * @param {object} data
 */
export const addNewNode = async (network, data = {}) => {
  if (data.isTask) {
    if (!data.node?.prop?._sys_creator) return [null, null];
    data.node.prop = createDefaultTempProp(data.node.prop, data.node?.prop?._sys_creator, 1, data);
  } else if (!!data.node?.prop?._sys_creator && !data.node.prop._sys_temp) {
    data.node.prop = createDefaultTempProp(data.node.prop, data.node.prop._sys_creator, 0, data);
  }
  // data.asyncUV = true;
  return await awaitWrap(network("/docapi/node/insert", {
    method: "POST",
    data
  }));
};

/**
 * 批量新增节点
 * @param network
 * @param data
 * @returns {Promise<*|*[]>}
 */
export const batchAddNewNode = async (network, data = {}) => {
  if (data.currentUser && Array.isArray(data.texts)) {
    const nodeList = [];
    const user_acl = {};
    let nodeProp = {};
    user_acl[data.currentUser] = default_user_acl;
    nodeProp["_sys_createTime"] = Math.round(+new Date() / 1000);
    nodeProp["_sys_user_acl"] = user_acl;
    nodeProp["_sys_creator"] = data.currentUser;
    if (data.sysTemp) {
      nodeProp["_sys_temp"] = data.sysTemp;
      nodeProp["_sys_task_status"] = data.sysTaskStatus || null;
    } else {
      if (data.isTask) {
        nodeProp = createDefaultTempProp(nodeProp, data.currentUser, 1, data);
      } else {
        nodeProp = createDefaultTempProp(nodeProp, data.currentUser, 0, data);
      }
    }
    data.texts.map(text => {
      const node = {
        node_id: generateId(),
        prop: nodeProp,
        title: text
      };
      nodeList.push(node);
    });
    data["node"] = nodeList;
    const res = await awaitWrap(network("/docapi/node/insert", {
      method: "POST",
      data
    }));
    if (res?.length > 1 && res[1]?.status === "ok") {
      res[1]["node_id"] = nodeList[0].node_id;
    }
    return res;
  }
  return [null, null];
};

/**
 * 编辑节点
 * @param {promise} network
 * @param {object} data
 */
export const editNode = async (network, data = {}) => {
  return await awaitWrap(network("/docapi/node/modify", {
    method: "POST",
    data
  }));
};

/**
 * 设置模板和状态为主题
 * @param {promise} network
 * @param {object} data
 * @returns
 */
export const setNodeActionToTheme = async (network, data = {}) => {
  return await awaitWrap(network("/docapi/clearAction", {
    method: "POST",
    data
  }));
};

/**
 * 设置节点模板和状态
 * @param {promise} network
 * @param {any} data
 * @returns
 */
export const setNodeAction = async (network, data = {}) => {
  return await awaitWrap(network("/docapi/setAction", {
    method: "POST",
    data
  }));
};

/**
 * 设置节点模板和状态
 * @param {promise} network
 * @param {org_id, node: [{node_id, startTime, endTime}, ]}
 * @returns
 */
export const setNodeActionTime = async (network, data = {}) => {
  return await awaitWrap(network("/docapi/setActionTime", {
    method: "POST",
    data
  }));
};

/**
 * 将节点移至回收站
 * @param network
 * @param data
 * @returns {Promise<*>}
 */
export const recycleNode = async (network, data = {}) => {
  return await awaitWrap(network("/docapi/moveToRecycle", {
    method: "POST",
    data
  }));
};

/**
 * 从回收站恢复节点
 * @param network
 * @param data
 * @returns {Promise<*>}
 */
export const recoverNode = async (network, data = {}) => {
  return await awaitWrap(network("/docapi/node/recover", {
    method: "POST",
    data
  }));
};

/**
 * 从回收站恢复节点到删除的位置
 * @param network
 * @param data
 * @returns {Promise<*>}
 */
export const recoverToOriginPath = async (network, data = {}) => {
  return await awaitWrap(network("/docapi/node/recoverToOriginPath", {
    method: "POST",
    data
  }));
};

/**
 * 永久删除节点
 * @param network
 * @param data
 * @returns {Promise<*>}
 */
export const removeNode = async (network, data = {}) => {
  return await awaitWrap(network("/docapi/node/remove", {
    method: "POST",
    data
  }));
};

/**
 * 修改任务节点颜色
 * @param network
 * @param data
 * @returns {Promise<*>}
 */
export const updateActionColor = async (network, data = {}) => {
  return await awaitWrap(network("/docapi/node/setColor", {
    method: "POST",
    data
  }));
};
/**
 * 设置节点权限
 * @param network
 * @param data
 * @returns {Promise<*>}
 */
export const setNodeAcl = async (network, data = {}) => {
  return await awaitWrap(network("/docapi/setAcl", {
    method: "POST",
    data
  }));
};
export const getTempMatchProps = async (network, data = {}) => {
  return [null, {
    status: "ok",
    data: []
  }];
};